import { EventNames } from '@farmasiet/frontendcore-reactcomponents';
import parse from 'date-fns/parse';
import * as React from 'react';
import { useEffect } from 'react';

import { RxSessionModalConfiguration } from '../../../Common/Layout/RxSessionModalConfiguration.csharp';
import { getFor } from '../../../ReactComponents/API';
import { CartViewModel } from '../../Cart/ViewModels/CartViewModel.csharp';
import { RxSessionExpiredModal } from './RxSessionExpiredModal';
import { RxSessionExpiringModal } from './RxSessionExpiringModal';
import { RxSessionLogoutModal } from './RxSessionLogoutModal';

export interface RxSessionModalProps {
    bankIdLoginUrl: string;
    bankIdLogoutUrl: string;
    ecomLogoutUrl: string;
    cartPageUrl: string;
    rxSessionModalConfiguration: RxSessionModalConfiguration;
    minNotificationThreshold: number;
    maxNotificationThreshold: number;
    acknowledgeRxsRemovedUrl: string;
    acknowledgeRxsWillRemoveUrl: string;
    checkConditionsForRxExpiringModal: string;
    checkConditionsForRxExpiredModal: string;
    requiresSecureLogin: boolean;
}

export const RxSessionModals = ({
    bankIdLoginUrl,
    bankIdLogoutUrl,
    ecomLogoutUrl,
    cartPageUrl,
    acknowledgeRxsRemovedUrl,
    acknowledgeRxsWillRemoveUrl,
    checkConditionsForRxExpiringModal,
    checkConditionsForRxExpiredModal,
    rxSessionModalConfiguration,
    minNotificationThreshold,
    maxNotificationThreshold,
    requiresSecureLogin
}: RxSessionModalProps) => {
    const [expirationDate, setExpirationDate] = React.useState(null as number | null);
    const [hasActiveSession, setHasActiveSession] = React.useState(false);
    const cartIdRef = React.useRef<number | null>(null);

    useEffect(() => {
        window.addEventListener(EventNames.CartChanged, handleCart);
        window.addEventListener(EventNames.CartValidated, handleCart);

        return () => {
            window.removeEventListener(EventNames.CartChanged, handleCart);
            window.removeEventListener(EventNames.CartValidated, handleCart);
        };
    }, []);

    const handleCart = ($event: CustomEventInit) => {
        if ($event && $event.detail) {
            const cart = $event.detail.cart as CartViewModel;
            if (!cart) {
                return;
            }
            cartIdRef.current = cart.id;

            if (rxSessionModalConfiguration === RxSessionModalConfiguration.DoNotShowModals) {
                return;
            }

            if (cart.hasRxItems && cart.rxSessionExpirationTime) {
                setHasActiveSession(true);
                const expirationDate = parse(cart.rxSessionExpirationTime, 'yyyy-MM-dd HH:mm:ss xxx', new Date()).getTime();
                setExpirationDate(expirationDate);
            } else {
                setHasActiveSession(false);
            }
        }
    };

    const validateBeforeShowing = (validateEndpointUrl: string): Promise<boolean> => {
        return new Promise<boolean>((resolve) => {
            if (cartIdRef.current) {
                getFor<boolean>(`${validateEndpointUrl}?cartId=${cartIdRef.current}`)
                    .then((result) => {
                        resolve(result);
                    })
                    .catch(() => resolve(false));
            } else {
                return resolve(false);
            }
        });
    };

    return (
        <>
            <RxSessionExpiredModal
                expirationDate={expirationDate}
                bankIdLoginUrl={bankIdLoginUrl}
                acknowledgeRxsRemovedUrl={acknowledgeRxsRemovedUrl}
                validateBeforeShowing={() => validateBeforeShowing(checkConditionsForRxExpiredModal)}
                requiresSecureLogin={requiresSecureLogin}
            />
            {rxSessionModalConfiguration === RxSessionModalConfiguration.ShowExpiringAndExpiredModals && hasActiveSession && (
                <RxSessionExpiringModal
                    expirationDate={expirationDate}
                    cartPageUrl={cartPageUrl}
                    validateBeforeShowing={() => validateBeforeShowing(checkConditionsForRxExpiringModal)}
                    minNotificationThresholdSeconds={minNotificationThreshold}
                    maxNotificationThresholdSeconds={maxNotificationThreshold}
                />
            )}
            <RxSessionLogoutModal
                acknowledgeRxsWillRemoveUrl={acknowledgeRxsWillRemoveUrl}
                bankIdLogoutUrl={bankIdLogoutUrl}
                ecomLogoutUrl={ecomLogoutUrl}
                cartPageUrl={cartPageUrl}
                validateBeforeShowing={() => validateBeforeShowing(checkConditionsForRxExpiringModal)}
            />
        </>
    );
};
